import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchIssues, fetchAllIssues, createIssue, type WebsiteIssue } from "../content/issues";
import { FolderSection } from "../components/issues/FolderSection";
import { IssuesTable } from "../components/issues/IssuesTable";
import { SubmitDocumentForm } from "../components/common/SubmitDocumentForm";

const PRIORITY_ORDER: Record<WebsiteIssue["priority"], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

interface FolderGroup {
  folder: string;
  issues: WebsiteIssue[];
}

function FolderNav({ groups }: { groups: FolderGroup[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="lg:w-64 flex-shrink-0">
      <div className="lg:sticky lg:top-20">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden w-full flex items-center justify-between px-4 py-2 bg-dark-card border border-dark-border rounded-lg text-warm-100"
        >
          <span className="font-medium">{t('common.jumpToFolder')}</span>
          <svg
            className={`w-5 h-5 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <nav className={`${isOpen ? "block" : "hidden"} lg:block mt-4 lg:mt-0`}>
          <div className="bg-dark-card border border-dark-border rounded-lg p-4">
            <h3 className="text-xs font-semibold text-warm-500 uppercase tracking-wider mb-3">
              {t('common.foldersHeading')}
            </h3>
            <ul className="space-y-1">
              {groups.map((group) => {
                const anchorId = group.folder ? `folder-${group.folder}` : "folder-root";
                const displayName = group.folder
                  ? group.folder.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                  : t('common.folderGeneral');

                return (
                  <li key={anchorId}>
                    <a
                      href={`#${anchorId}`}
                      className="flex items-center justify-between px-2 py-1.5 rounded text-sm text-warm-200 hover:text-warm-50 hover:bg-dark-hover transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      <span>{displayName}</span>
                      <span className="text-xs text-warm-500">{group.issues.length}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      </div>
    </div>
  );
}

export function KnownIssues() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [issues, setIssues] = useState<WebsiteIssue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTable, setShowTable] = useState(searchParams.get("view") === "all");
  const [allIssues, setAllIssues] = useState<WebsiteIssue[]>([]);
  const [allIssuesLoading, setAllIssuesLoading] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);

  const refetchIssues = useCallback(() => {
    setFetchKey((k) => k + 1);
    setAllIssues([]);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchIssues()
      .then((response) => {
        setIssues(response.issues);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('knownIssues.failedToLoad'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fetchKey, t]);

  useEffect(() => {
    if (showTable && allIssues.length === 0 && !allIssuesLoading) {
      setAllIssuesLoading(true);
      fetchAllIssues()
        .then((response) => {
          setAllIssues(response.issues);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : t('knownIssues.failedToLoadAll'));
        })
        .finally(() => {
          setAllIssuesLoading(false);
        });
    }
  }, [showTable, allIssues.length, allIssuesLoading, t]);

  const groups = useMemo(() => {
    const folderMap = new Map<string, WebsiteIssue[]>();

    for (const issue of issues) {
      const folder = issue.folder;
      const existing = folderMap.get(folder);
      if (existing) {
        existing.push(issue);
      } else {
        folderMap.set(folder, [issue]);
      }
    }

    const sorted: FolderGroup[] = Array.from(folderMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([folder, folderIssues]) => ({
        folder,
        issues: folderIssues.sort(
          (a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
        ),
      }));

    return sorted;
  }, [issues]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-dark-bg flex items-center justify-center">
        <p className="text-warm-400">{t('knownIssues.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-dark-bg flex items-center justify-center">
        <p className="text-warm-400">{t('knownIssues.failedToLoadWithError', { error })}</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ backgroundColor: "#1a1816" }}>
      {/* Ambient radial gradients — red/rose theme */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] opacity-[0.07]"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(220, 38, 38, 0.8) 0%, rgba(185, 28, 28, 0.6) 40%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute top-[500px] -left-[200px] w-[800px] h-[800px] opacity-[0.04]"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(239, 68, 68, 0.8) 0%, rgba(220, 38, 38, 0.6) 50%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute top-[400px] -right-[200px] w-[800px] h-[800px] opacity-[0.04]"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(185, 28, 28, 0.8) 0%, rgba(153, 27, 27, 0.6) 50%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute bottom-0 left-1/3 w-[600px] h-[400px] opacity-[0.03]"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(220, 38, 38, 0.8) 0%, rgba(185, 28, 28, 0.6) 50%, transparent 70%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <h1 className="text-4xl font-bold text-warm-50 mb-3">{t('knownIssues.title')}</h1>
          <p className="text-warm-400 text-lg">
            {t('knownIssues.subtitle')}
          </p>
        </header>

        <SubmitDocumentForm
          type="issue"
          buttonLabel={t('knownIssues.reportIssue')}
          titlePlaceholder={t('knownIssues.titlePlaceholder')}
          bodyPlaceholder={t('knownIssues.bodyPlaceholder')}
          accentColor="red"
          onSubmit={createIssue}
          onSuccess={refetchIssues}
        />

        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => setShowTable(false)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              !showTable
                ? "bg-red-500/20 text-red-200 border border-red-500/30"
                : "text-warm-500 hover:text-warm-300"
            }`}
          >
            {t('knownIssues.viewCards')}
          </button>
          <button
            onClick={() => setShowTable(true)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              showTable
                ? "bg-red-500/20 text-red-200 border border-red-500/30"
                : "text-warm-500 hover:text-warm-300"
            }`}
          >
            {t('knownIssues.viewAllIssues')}
          </button>
        </div>

        {showTable ? (
          allIssuesLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-warm-400">{t('knownIssues.loadingAll')}</p>
            </div>
          ) : (
            <IssuesTable issues={allIssues} />
          )
        ) : (
          <div className="lg:flex lg:gap-8">
            <div className="flex-1 min-w-0">
              <div className="space-y-8">
                {groups.map((group) => (
                  <FolderSection key={group.folder} folder={group.folder} issues={group.issues} />
                ))}
              </div>

              {groups.length === 0 && (
                <p className="text-warm-500 text-center py-12">{t('knownIssues.noIssues')}</p>
              )}
            </div>

            {groups.length > 0 && <FolderNav groups={groups} />}
          </div>
        )}
      </div>
    </div>
  );
}
